// languagetools:disable
/* cspell:disable */

'use client'

import { useCallback, useRef, useState } from 'react'
import type { Channel as ChannelInstance, Event, MessageResponse } from 'stream-chat'
import { StreamChat } from 'stream-chat'

import { useChatRuntimeConfig } from '../runtime/chat-runtime-config'

const NOTIFICATION_DEDUP_WINDOW_MS = 30000

/**
 * Bootstraps the StreamChat singleton and manages all client-level event listeners
 * for in-app and browser push notifications.
 *
 * Responsibilities:
 * - Creates (or returns the existing) StreamChat singleton via `getInstance`
 * - Tracks the currently active chat channel so notifications are suppressed
 *   for messages the user is already viewing
 * - Fires browser push notifications for `message.new` and
 *   `notification.message_new` events from other users
 * - Delegates active-chat persistence and cross-tab sync to the host-provided
 *   `activeChannelState` adapter
 */
export function useChatClient() {
  const {
    activeChannelState,
    createNotification,
    getPathname,
    isInboxPath,
    loggedInUserID,
    navigateToInboxChat,
    streamAppKey
  } = useChatRuntimeConfig()
  const pathname = getPathname()

  // `getInstance` returns the existing client if one has already been created with
  // this app key, ensuring only one WebSocket connection is opened per tab.
  const [client] = useState(() =>
    StreamChat.getInstance(streamAppKey ?? '', {
      enableWSFallback: true,
      timeout: 30000
    })
  )

  const [activeChatID, setActiveChatID] = useState<string | null>(() => {
    return activeChannelState.get?.() ?? null
  })

  // Refs to avoid stale closures in event handlers registered once.
  // Updating the ref on every render is cheaper than re-registering handlers.
  const activeChatIDRef = useRef(activeChatID)
  activeChatIDRef.current = activeChatID

  const pathnameRef = useRef(pathname)
  pathnameRef.current = pathname

  // Prevent duplicate browser notifications when Stream emits multiple related events
  // (e.g. message.new + notification.message_new) for the same message.
  const recentlyNotifiedMessageIDsRef = useRef<Map<string, number>>(new Map())

  /**
   * Returns true if the message carries an `action_type` field, indicating it is
   * a system/automated action message rather than a user-authored chat message.
   * Action messages are excluded from notifications.
   */
  const isActionMessage = (event: Event): boolean => {
    const message = event.message as MessageResponse & { action_type?: string }
    return !!message?.action_type
  }

  /**
   * Fires a browser push notification for an incoming message.
   * `includeSilent` controls whether the `silent` flag from the message payload
   * is forwarded — used to suppress sound on `message.new` vs `notification.message_new`.
   */
  const createMessageNotification = useCallback(
    (event: Event, includeSilent: boolean) => {
      if (!event.message?.user) return

      const messageID = event.message.id || ''
      if (messageID) {
        const now = Date.now()
        const cutoff = now - NOTIFICATION_DEDUP_WINDOW_MS
        const trackedIDs = recentlyNotifiedMessageIDsRef.current

        for (const [trackedMessageID, timestamp] of trackedIDs.entries()) {
          if (timestamp < cutoff) {
            trackedIDs.delete(trackedMessageID)
          }
        }

        if (trackedIDs.has(messageID)) {
          return
        }

        trackedIDs.set(messageID, now)
      }

      createNotification?.({
        body: event.message.text || '',
        data: {
          messageID: event.message.id,
          ...(includeSilent && { silent: event.message.silent })
        },
        notificationHandler: () => {
          navigateToInboxChat?.({
            chatID: event.channel_id || '',
            chatType: event.channel_type || ''
          })
        },
        title: event.message.user?.name ? `${event.message.user.name} sent a message` : ''
      })
    },
    [createNotification, navigateToInboxChat]
  )

  /**
   * Handles `message.new` events fired when a message arrives in a channel the
   * user is already a member of and has open.
   * Notification is skipped if:
   * - The message is an action message
   * - The sender is the logged-in user
   * - The message arrived in the currently active chat channel
   */
  const handleNewMessage = useCallback(
    (event: Event) => {
      if (isActionMessage(event)) return
      if (
        loggedInUserID !== event?.user?.id &&
        event.message &&
        event.message.user &&
        event.channel_id !== activeChatIDRef.current
      ) {
        createMessageNotification(event, true)
      }
    },
    [loggedInUserID, createMessageNotification]
  )

  /**
   * Handles `notification.message_new` events fired when a message arrives in a
   * channel the user is a member of but does not currently have open (background tab).
   * Similar suppression rules as `handleNewMessage` but without the `silent` flag.
   */
  const handleNewMessageNotification = useCallback(
    (event: Event) => {
      if (isActionMessage(event)) return
      if (
        event.message &&
        event.message.user &&
        loggedInUserID !== event?.message.user.id &&
        event.channel_id !== activeChatIDRef.current
      ) {
        createMessageNotification(event, false)
      }
    },
    [loggedInUserID, createMessageNotification]
  )

  /**
   * Keeps `activeChatID` in sync across host-managed shared state listeners.
   * Only processes updates that did not originate from an inbox-routed URL to
   * avoid conflicts when the inbox tab itself manages the active chat state.
   */
  const handleActiveChatStateChange = useCallback(
    (nextActiveChatID: string | null, sourcePathname: string | null) => {
      if (!isInboxPath(sourcePathname || '')) {
        setActiveChatID(nextActiveChatID)
      }
    },
    [isInboxPath]
  )
  const removeActiveChatStateListenerRef = useRef<(() => void) | null>(null)

  /**
   * Attaches all client-level Stream Chat listeners and host-provided active-chat
   * sync listeners. Called once from `ChatProvider` on mount.
   */
  const addListeners = useCallback(() => {
    client.on('message.new', handleNewMessage)
    client.on('notification.message_new', handleNewMessageNotification)
    removeActiveChatStateListenerRef.current = activeChannelState.subscribe?.(handleActiveChatStateChange) ?? null
  }, [activeChannelState, client, handleActiveChatStateChange, handleNewMessage, handleNewMessageNotification])

  /**
   * Removes all client-level event listeners attached by `addListeners`.
   * Called from `ChatProvider` on unmount to prevent memory leaks.
   */
  const removeListeners = useCallback(() => {
    client.off('message.new', handleNewMessage)
    client.off('notification.message_new', handleNewMessageNotification)
    removeActiveChatStateListenerRef.current?.()
    removeActiveChatStateListenerRef.current = null
  }, [client, handleNewMessage, handleNewMessageNotification])

  /**
   * Updates the currently active chat channel.
   * Persists the channel ID via the host adapter outside inbox routes so other tabs
   * can suppress duplicate notifications.
   */
  const setActiveChat = useCallback(
    (channel: ChannelInstance | null) => {
      const newActiveChatID = channel?.id || null
      setActiveChatID(newActiveChatID)

      if (!isInboxPath(pathnameRef.current)) {
        activeChannelState.set?.(newActiveChatID)
      }
    },
    [activeChannelState, isInboxPath]
  )

  return {
    activeChatID,
    addListeners,
    client,
    removeListeners,
    setActiveChat
  }
}
